import { Link } from "react-router-dom";
import useDepartmentDoctors from "../hooks/useDepartmentDoctors";
import DoctorCard from "./DoctorCard";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import SEO from "../pages/SEO"
import { useAppointment } from "../context/AppointmentContext"
import {
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  Users,
  Award,
  Clock,
  Heart,
  Activity,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";

export default function SportsMedicinePage() {
  const { open: openAppointment } = useAppointment();
  const doctors = [];


  const subspecialties = [
    {
      title: "Sports Injury Management",
      description: "Sprains, strains, ligament tears & joint injuries.",
      image: "/SportsInjuryManagement.jpg",
    },
    {
      title: "Sports Orthopaedics",
      description: "ACL/PCL tears, meniscus injuries & shoulder instability.",
      image: "/SportsOrthopaedics.jpg",
    },
    {
      title: "Sports Physiotherapy & Rehab",
      description:
        "Strengthening, mobility training & return-to-sport programs.",
      image: "/SportsPhysiotherapy&Rehab.jpg",
    },
    {
      title: "Performance Enhancement Training",
      description:
        "Fitness assessment, conditioning & injury prevention.",
      image: "/PerformanceEnhancementTraining.jpg",
    },
    {
      title: "Pediatric Sports Medicine",
      description:
        "Sports injuries in children & adolescent athletes.",
      image: "/PediatricSportsMedicine.jpg",
    },
    {
      title: "Sports Nutrition",
      description:
        "Diet plans for performance, endurance & muscle recovery.",
      image: "/SportsNutrition.jpg",
    },
  ];

  const treatments = [
    "ACL & PCL Reconstruction",
    "Arthroscopy for Knee, Shoulder & Ankle",
    "Treatment for Muscle Tears & Ligament Injuries",
    "Sports Fracture Management",
    "PRP (Platelet-Rich Plasma) Therapy for Tendon & Joint Injuries",
    "Shoulder & Rotator Cuff Repair",
    "Runner's Knee, Tennis Elbow & Shin Splints Treatment",
    "Customized Sports Rehabilitation Programs",
    "Gait Analysis & Biomechanical Assessment",
  ];

  const whyChoose = [
    {
      icon: Users,
      title: "Expert Sports Physicians",
      description: "Expert sports physicians & orthopaedic surgeons",
    },
    {
      icon: Award,
      title: "Advanced Arthroscopy",
      description: "Advanced arthroscopy & minimally invasive procedures",
    },
    {
      icon: Activity,
      title: "Sports Rehab Center",
      description:
        "Dedicated physiotherapy & sports rehab center",
    },
    {
      icon: Heart,
      title: "Return-to-Play Plans",
      description:
        "Customized recovery & return-to-play plans",
    },
    {
      icon: Clock,
      title: "Fitness Assessment",
      description:
        "Modern fitness assessment tools & gait analysis",
    },
  ];

  const articles = [
    {
      title: "Top 5 Common Sports Injuries and How to Prevent Them",
      excerpt:
        "Learn about the most frequent sports injuries and effective prevention strategies to stay active and safe.",
    },
    {
      title:
        "How Physiotherapy Helps Athletes Recover Faster",
      excerpt:
        "Discover the role of targeted physiotherapy in accelerating recovery and enhancing athletic performance.",
    },
    {
      title: "ACL Tears — Causes, Treatment & Recovery Timeline",
      excerpt:
        "Comprehensive guide to understanding ACL injuries, surgical options, and expected recovery periods.",
    },
    {
      title:
        "What Athletes Should Know About Sports Nutrition",
      excerpt:
        "Essential nutrition guidance for optimizing performance, endurance, and muscle recovery in athletes.",
    },
  ];

  const faqs = [
    {
      question: "Do you treat ligament and muscle injuries?",
      answer:
        "Yes, we specialize in managing ligament tears, sprains, strains & sports-related muscle injuries.",
    },
    {
      question: "Is arthroscopy available at MAMS Hospitals?",
      answer:
        "Yes, advanced arthroscopic procedures are performed for knee, shoulder & ankle injuries.",
    },
    {
      question: "Do you offer rehabilitation for athletes?",
      answer:
        "Yes, our physiotherapy and sports rehab programs help athletes return to peak performance safely.",
    },
  ];
  const schema = [
  {
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    "name": "MAMS Hospitals Sports Medicine Department",
    "url": "https://mamshospitals.com/sports-medicine",
    "logo": "https://mamshospitals.com/MAMS-Logo.png",
    "image": "https://mamshospitals.com/SportsMedicine.jpg",
    "description": "MAMS Hospitals provides advanced sports medicine treatment in Hyderabad including sports injury management, arthroscopy, ligament reconstruction, physiotherapy, PRP therapy, rehabilitation, and athlete performance care.",
    "medicalSpecialty": "Sports Medicine",
    "telephone": "+91-7399999981",
    "priceRange": "$$",

    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Bachupally",
      "addressLocality": "Hyderabad",
      "addressRegion": "Telangana",
      "postalCode": "500090",
      "addressCountry": "IN"
    },

    "availableService": [
      {
        "@type": "MedicalProcedure",
        "name": "ACL & PCL Reconstruction"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Sports Injury Rehabilitation"
      },
      {
        "@type": "MedicalProcedure",
        "name": "PRP Therapy"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Arthroscopy Procedures"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Sports Physiotherapy"
      }
    ],

    "sameAs": [
      "https://www.facebook.com/MAMSHospitals",
      "https://www.instagram.com/mams_hospitals"
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Do you treat sports injuries and ligament tears?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, MAMS Hospitals specializes in sports injuries including ligament tears, muscle injuries, fractures, and joint instability."
        }
      },
      {
        "@type": "Question",
        "name": "Is arthroscopy available at MAMS Hospitals?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, advanced arthroscopic procedures are available for knee, shoulder, ankle, and sports-related joint injuries."
        }
      },
      {
        "@type": "Question",
        "name": "Do you provide sports rehabilitation programs?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, MAMS Hospitals provides physiotherapy, sports rehabilitation, gait analysis, and return-to-play programs."
        }
      }
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Sports Medicine Department - MAMS Hospitals",
    "url": "https://mamshospitals.com/sports-medicine",
    "description": "Best sports medicine hospital in Hyderabad offering sports injury treatment, ACL reconstruction, arthroscopy, physiotherapy, PRP therapy, and athlete rehabilitation.",
    "keywords": [
      "Sports Medicine Hospital Hyderabad",
      "Best Sports Injury Doctor Hyderabad",
      "ACL Reconstruction Hyderabad",
      "Sports Physiotherapy Hyderabad",
      "Arthroscopy Hyderabad",
      "PRP Therapy Hyderabad",
      "Sports Medicine Hospital Bachupally"
    ]
  }
];

  return (
  <>
  <SEO
      title="Best Sports Medicine Hospital in Hyderabad | Sports Injury Specialists | MAMS Hospitals"
      description="MAMS Hospitals offers advanced sports medicine treatment in Hyderabad including sports injury management, arthroscopy, ligament reconstruction, PRP therapy, physiotherapy, and athlete rehabilitation."
      keywords="sports medicine hospital Hyderabad,
  best sports injury doctor Hyderabad,
  ACL reconstruction Hyderabad,
  sports physiotherapy Hyderabad,
  arthroscopy Hyderabad,
  PRP therapy Hyderabad,
  sports injury treatment Hyderabad,
  ligament tear treatment Hyderabad,
  sports rehabilitation Hyderabad,
  athlete recovery Hyderabad,
  sports orthopaedics Hyderabad,
  sports medicine hospital Bachupally,
  MAMS Hospitals Sports Medicine"
      schema={schema}
    />
    <div className="flex flex-col min-h-screen">

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-50 via-white to-blue-50 py-12 md:py-24">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">

            <div>
              <div className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                Best Sports Medicine Hospital in Hyderabad
              </div>

              <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
                From Injury to Recovery — Expert Sports Care
              </h1>

              <p className="text-lg text-gray-700 mb-8">
                MAMS Hospitals is recognized as one of the Best Sports Medicine Hospitals in Hyderabad, offering specialized care for athletes, fitness enthusiasts, and individuals with sports-related injuries.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">

                <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8" size="lg" onClick={openAppointment}>
                    Book Appointment
                  </Button>

                <Link to="tel:+91-7399999981">
                  <Button
                    className="border-teal-600 text-teal-600 hover:bg-teal-50 rounded-full px-8"
                    variant="outline"
                    size="lg"
                  >
                    <Phone className="mr-2 h-5 w-5" />
                    Call Us
                  </Button>
                </Link>

              </div>
            </div>

            <div className="relative">
              <div className="absolute -top-4 -right-4 bg-teal-600 text-white p-6 rounded-2xl shadow-xl">
                <div className="text-3xl font-bold">Expert</div>
                <div className="text-sm">Sports Care</div>
              </div>

              <img
                src="/SportsMedicine.jpg"
                alt="Sports Medicine at MAMS Hospital"
                className="rounded-2xl shadow-2xl w-full"
              canonicalUrl="https://mamshospitals.com/departments/sports-medicine"
      />
            </div>

          </div>
        </div>
      </section>

      {/* Subspecialties */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container max-w-6xl mx-auto px-4">

          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Sub-Specialities in Sports Medicine
          </h2>

          <p className="text-gray-600 text-center max-w-3xl mx-auto mb-12">
            Comprehensive sports medicine services for athletes & active individuals
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {subspecialties.map((item, idx) => (
              <Card
                key={idx}
                className="group hover:shadow-xl border-gray-200 hover:border-teal-500 transition"
              >
                <CardContent className="p-0">

                  <div className="relative h-48 overflow-hidden rounded-t-lg">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition"
                    />
                  </div>

                  <div className="p-6">
                    <h3 className="text-xl font-bold">{item.title}</h3>
                    <p className="text-gray-600 mt-2">{item.description}</p>
                  </div>

                </CardContent>
              </Card>
            ))}
          </div>

        </div>
      </section>

      {/* Treatments */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container max-w-6xl mx-auto px-4">

          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Treatments & Procedures
          </h2>

          <p className="text-gray-600 text-center mb-12">
            Advanced sports treatments for optimal recovery & performance
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            {treatments.map((t, idx) => (
              <div key={idx} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm">
                <CheckCircle2 className="text-teal-600 h-6 w-6 mt-1" />
                <span className="text-gray-700 font-medium">{t}</span>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Why Choose */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container max-w-6xl mx-auto px-4">

          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Why Choose MAMS Hospitals for Sports Medicine
          </h2>

          <p className="text-gray-600 text-center mb-12">
            Expert sports injury treatment & rehabilitation
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChoose.map((item, idx) => (
              <Card key={idx} className="border-gray-200 hover:shadow-lg transition">
                <CardContent className="p-6">

                  <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                    <item.icon className="text-teal-600 h-7 w-7" />
                  </div>

                  <h3 className="text-xl font-bold">{item.title}</h3>
                  <p className="text-gray-600 mt-2">{item.description}</p>

                </CardContent>
              </Card>
            ))}
          </div>

        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container max-w-4xl mx-auto px-4">

          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Frequently Asked Questions
          </h2>

          <p className="text-gray-600 text-center mb-12">
            Get answers to common sports medicine concerns
          </p>

          <Accordion type="single" collapsible>
            {faqs.map((faq, idx) => (
              <AccordionItem key={idx} value={`item-${idx}`}>
                <AccordionTrigger>{faq.question}</AccordionTrigger>
                <AccordionContent>{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

        </div>
      </section>

      {/* Contact & Appointment Section */}
      <section className="py-16 px-0 bg-apple-teal text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">Book an Appointment</h2>
            <p className="text-white/90 max-w-2xl mx-auto">
              Your heart health is our top priority. Book an appointment today with Hyderabad's trusted experts at MAMS Hospitals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Phone className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Call Us</h3>
                <a href="tel:+917399999981" className="text-white/90 hover:text-white">
                  +91-7399999981
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Mail className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Email Us</h3>
                <a href="mailto: info@mamshospitals.com" className="text-white/90 hover:text-white">
                   info@mamshospitals.com
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <MapPin className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Visit Us</h3>
                <p className="text-white/90">Bachupally, Miyapur</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            {/* <Link href="/book-appointment">
              <Button size="lg" variant="secondary" className="rounded-full">
                <Calendar className="mr-2 h-5 w-5" />
                Schedule Your Appointment
              </Button>
            </Link> */}
          </div>
        </div>
      </section>

      {/* Emergency Banner */}
      <section className="py-8 px-0 bg-red-600 text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Clock className="h-8 w-8" />
              <div>
                <h3 className="text-xl font-bold">24/7 Emergency Care</h3>
                <p className="text-white/90">Fully equipped emergency department</p>
              </div>
            </div>
            <a href="tel:+917399999981">
              <Button size="lg" variant="secondary" className="w-full sm:w-auto rounded-full bg-white text-red-600 hover:bg-gray-100">
                <Phone className="mr-2 h-5 w-5" />
                Call Emergency
              </Button>
            </a>
          </div>
        </div>
      </section>

    </div>
    </>
  );
}
